import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { type FC } from 'react';

import {
  ACTIVITY_DIMENSION_FALLBACK,
  ACTIVITY_DIMENSION_NAMES,
  ACTIVITY_DIMENSION_VALUES,
  ACTIVITY_OBSERVED_DIMENSION_NAMES,
  type ActivityDimensionName,
  type ActivityDimensionValue,
  type CompleteDimensionValues,
} from '@okvc/schema';

/** Any block of readings: the AI's six for the patient, or the five stage 1 observes per person. */
export type ActivityDimensionBlock = Partial<Record<ActivityDimensionName, ActivityDimensionValue<string>>>;

const UNSURE_VALUES = new Set<string>(Object.values(ACTIVITY_DIMENSION_FALLBACK));

export function dimensionLabel(value: string): string {
  return value.replace(/_/g, ' ');
}

/** `lying · bedroom · bed · likely asleep · resting` — the observed dimensions on one line. */
export function formatDimensionsLine(dimensions: ActivityDimensionBlock): string {
  return ACTIVITY_OBSERVED_DIMENSION_NAMES
    .flatMap((name) => {
      const reading = dimensions[name];
      return reading ? [dimensionLabel(reading.value)] : [];
    })
    .join(' · ');
}

function readingsOf(dimensions: ActivityDimensionBlock) {
  return ACTIVITY_DIMENSION_NAMES.flatMap((name) => {
    const reading = dimensions[name];
    return reading ? [{ name, ...reading }] : [];
  });
}

interface ActivityDimensionChipsProps {
  dimensions: ActivityDimensionBlock;
}

/**
 * Read-only: one small chip per dimension the block carries. Values the model
 * could not settle on (`unknown`, `uncertain`) are dimmed so the eye lands on
 * what it did claim; the confidence lives in the tooltip.
 */
const ActivityDimensionChips: FC<ActivityDimensionChipsProps> = ({ dimensions }) => (
  <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 0.25 }}>
    {readingsOf(dimensions).map(({ name, value, confidence }) => (
      <Tooltip key={name} arrow title={`${name}: ${dimensionLabel(value)} · ${Math.round(confidence * 100)}%`}>
        <Chip
          size={'small'}
          variant={'outlined'}
          label={dimensionLabel(value)}
          sx={{
            height: 18,
            fontSize: 10,
            '& .MuiChip-label': { px: 0.75 },
            ...(UNSURE_VALUES.has(value) && { color: 'text.disabled', borderStyle: 'dashed' }),
          }}
        />
      </Tooltip>
    ))}
  </Stack>
);

interface ActivityDimensionPickerProps {
  values: CompleteDimensionValues;
  aiValues: CompleteDimensionValues;
  onChange: (name: ActivityDimensionName, value: string) => void;
}

/** Editable: every option per dimension, the reviewer's choice filled and the AI's marked. */
export const ActivityDimensionPicker: FC<ActivityDimensionPickerProps> = ({ values, aiValues, onChange }) => (
  <Stack spacing={1}>
    {ACTIVITY_DIMENSION_NAMES.map((name) => (
      <Box key={name}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
          {name}
        </Typography>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
          {ACTIVITY_DIMENSION_VALUES[name].map((option) => {
            const selected = values[name] === option;
            const fromAi = aiValues[name] === option;
            return (
              <Chip
                key={option}
                size="small"
                label={dimensionLabel(option)}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                icon={fromAi ? <AutoAwesomeIcon sx={{ fontSize: 14 }} /> : undefined}
                onClick={() => onChange(name, option)}
              />
            );
          })}
        </Stack>
      </Box>
    ))}
  </Stack>
);

export default ActivityDimensionChips;
